import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Typography, Box, Divider, Chip, Grid } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { Paper } from 'shared/ui/Paper';
import { ColorPalette } from 'shared/consts/colorPalette';
import { AutoconnectByLabelsEnum } from 'entities/CustomModel/types';
import { SettingsSchemaType } from '../../validationSchema';

const PartnersAssociation = () => {
	const { t } = useTranslation();
	const { values, setFieldValue, errors } = useFormikContext<SettingsSchemaType>();

	const onDragEnd = (data: DropResult) => {
		const newItems = values.settings?.autoconnectByLabels ? [...values.settings.autoconnectByLabels] : [];
		newItems[Number(data.destination?.droppableId)] = data.draggableId as AutoconnectByLabelsEnum;

		setFieldValue('settings.autoconnectByLabels', newItems);
	};

	const removeItem = (index: number) => {
		const newItems = values.settings?.autoconnectByLabels ? [...values.settings.autoconnectByLabels] : [];
		setFieldValue(
			'settings.autoconnectByLabels',
			newItems.map((item, i) => (i === index ? '' : item)),
		);
	};

	return (
		<Paper sx={{ width: '100%', padding: 4 }}>
			<Grid container direction="column" gap={2.5}>
				<Typography variant="h5" color={ColorPalette.astronaut}>
					{t('custom_model.settings.partners_title')}
				</Typography>

				<Divider color={ColorPalette.periwinkle} />

				<Typography variant="body1" color={ColorPalette.astronaut}>
					{t('custom_model.settings.partners_description')}
				</Typography>

				<Typography variant="h6" color={ColorPalette.astronaut}>
					{t('custom_model.settings.partners_orders')}
				</Typography>

				<Typography variant="body1" color={ColorPalette.astronaut}>
					{t('custom_model.settings.partners_priority')}
				</Typography>

				<DragDropContext onDragEnd={onDragEnd}>
					<Droppable droppableId="droppable">
						{(provided) => (
							<Grid
								container
								gap={2}
								{...provided.droppableProps}
								ref={provided.innerRef}
								sx={{ minHeight: '32px' }}
							>
								{Object.values(AutoconnectByLabelsEnum)
									.filter((label) => !values.settings.autoconnectByLabels?.includes(label))
									.map((label, index) => (
										<Draggable key={label} draggableId={label} index={index}>
											{(provided) => (
												<Chip
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													key={label}
													label={label}
													variant="outlined"
												/>
											)}
										</Draggable>
									))}
							</Grid>
						)}
					</Droppable>

					{errors.settings?.autoconnectByLabels && (
						<Typography variant={'caption'} color={ColorPalette.violetRed}>
							{t(errors.settings?.autoconnectByLabels)}
						</Typography>
					)}

					<Grid container direction="column" gap={1}>
						{values.settings.autoconnectByLabels?.map((label, index) => (
							<Fragment key={index}>
								<Grid container gap={1} alignItems="center" wrap="nowrap">
									<Typography>{index + 1}.</Typography>

									<Droppable droppableId={`${index}`}>
										{(provided) => (
											<Box
												{...provided.droppableProps}
												ref={provided.innerRef}
												sx={{
													height: '32px',
													width: '100%',
												}}
											>
												{label && (
													<Chip
														label={label}
														variant="outlined"
														onDelete={() => removeItem(index)}
														color="error"
													/>
												)}
											</Box>
										)}
									</Droppable>
								</Grid>
								{values.settings?.autoconnectByLabels?.length !== index + 1 && (
									<Divider color={ColorPalette.periwinkle} />
								)}
							</Fragment>
						))}
					</Grid>
				</DragDropContext>
			</Grid>
		</Paper>
	);
};

export default PartnersAssociation;
